import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import Latex from 'react-latex-next';
import 'katex/dist/katex.min.css';
import { API_BASE_URL } from '../../config';

const DIFFICULTY_LEVELS = ['Easy', 'Medium', 'Hard'];

export default function LiveTest() {
  const { instituteId, testId } = useParams();
  const navigate = useNavigate();
  
  // -- Test Configuration & Base State --
  const [testConf, setTestConf] = useState(null);
  const [adaptiveRules, setAdaptiveRules] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [scoreData, setScoreData] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);

  // -- Dynamic Adaptive State --
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({}); // { questionId: "Option A" }
  const [currentDifficulty, setCurrentDifficulty] = useState('Medium');
  const [stageCorrectCount, setStageCorrectCount] = useState(0);
  const [isFetchingNext, setIsFetchingNext] = useState(false);
  const [windowLeaveCount, setWindowLeaveCount] = useState(0);
  const [totalOutsideTime, setTotalOutsideTime] = useState(0);

  // Refs for tracking mutable state without re-render cycles in complex logic
  const answersRef = useRef({});
  const seenIdsRef = useRef([]);

  const fetchQuestion = async (difficulty, subject, targetClass, chapter) => {
    try {
      const params = {
        subject: subject,
        targetClass: targetClass,
        difficulty: difficulty,
        chapter: chapter, // Added chapter filter
        size: 50 // Get a pool
      };
      const res = await axios.get(`${API_BASE_URL}/api/questions`, { params });
      let pool = res.data || [];
      
      // Filter out seen IDs
      const available = pool.filter(q => !seenIdsRef.current.includes(q.id));
      
      if (available.length === 0) {
        // Fallback: If no specific difficulty left, try any difficulty
        const fallbackRes = await axios.get(`${API_BASE_URL}/api/questions`, { 
           params: { subject, targetClass, size: 100 } 
        });
        const anyAvailable = (fallbackRes.data || []).filter(q => !seenIdsRef.current.includes(q.id));
        if (anyAvailable.length === 0) return null;
        return anyAvailable[Math.floor(Math.random() * anyAvailable.length)];
      }

      return available[Math.floor(Math.random() * available.length)];
    } catch (err) {
      console.error("Fetch error:", err);
      return null;
    }
  };

  const fetchTestDetails = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/tests/${testId}`);
      const test = res.data;
      setTestConf(test);
      setTimeLeft(test.durationMins * 60);

      const qIds = test.questionIds;

      // Case 1: Standard Pre-defined Test
      if (qIds && qIds.length > 0) {
        // Use high-performance bulk fetch instead of multiple concurrent requests
        const qResponses = await axios.post(`${API_BASE_URL}/api/questions/list`, qIds);
        const fetched = qResponses.data;
        
        if (!fetched || fetched.length === 0) {
          toast.error("Could not retrieve question details.");
          setIsInitializing(false);
          return;
        }

        setQuestions(fetched);
        seenIdsRef.current = fetched.map(q => q.id);
        setIsInitializing(false);
        return;
      }

      // Case 2: Dynamic Adaptive Test
      if (test.type === 'Adaptive') {
        const rules = test.adaptiveConfigJson ? JSON.parse(test.adaptiveConfigJson) : {};
        setAdaptiveRules(rules);
        
        const startDiff = rules.startDifficulty || 'Medium';
        setCurrentDifficulty(startDiff);

        const firstQ = await fetchQuestion(startDiff, test.subjectFocus, test.targetClass, test.chapterFocus);
        if (firstQ) {
          setQuestions([firstQ]);
          seenIdsRef.current = [firstQ.id];
        } else {
          toast("No questions available in the bank for this subject.");
        }
      } else {
        toast("This test has no questions assigned.");
      }
      setIsInitializing(false);
    } catch (err) {
      console.error(err);
      toast("Failed to load test data.");
      setIsInitializing(false);
    }
  }, [testId]);

  useEffect(() => {
    fetchTestDetails();
  }, [fetchTestDetails]);

  const handleSubmit = useCallback(() => {
    if (isSubmitted) return;
    setIsSubmitted(true);
    
    let correct = 0;
    let wrong = 0;
    let unattempted = 0;

    const marksForCorrect = 4;
    const marksForWrong = -1;

    // Subject breakdown logic
    const breakdowns = {
      Physics: { score: 0, max: 0 },
      Chemistry: { score: 0, max: 0 },
      Mathematics: { score: 0, max: 0 },
      Biology: { score: 0, max: 0 },
      General: { score: 0, max: 0 }
    };

    questions.forEach(q => {
      const subject = q.subject || 'General';
      const userAns = answers[q.id];
      
      let key = 'General';
      if (subject.toLowerCase().includes('physic')) key = 'Physics';
      else if (subject.toLowerCase().includes('chem')) key = 'Chemistry';
      else if (subject.toLowerCase().includes('math')) key = 'Mathematics';
      else if (subject.toLowerCase().includes('biol')) key = 'Biology';

      breakdowns[key].max += marksForCorrect;

      if (!userAns) {
        unattempted++;
      } else if (userAns === q.correctOption) {
        correct++;
        breakdowns[key].score += marksForCorrect;
      } else {
        wrong++;
        breakdowns[key].score += marksForWrong;
      }
    });

    const totalScore = (correct * marksForCorrect) + (wrong * marksForWrong);
    const maxScore = (testConf.questionLimit || questions.length) * marksForCorrect;
    const accuracy = correct + wrong > 0 ? Math.round((correct / (correct + wrong)) * 100) : 0;

    setScoreData({ correct, wrong, unattempted, totalScore, maxScore, accuracy });

    // Sync results to student profile
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        if (user.email && testConf) {
          const resultPayload = {
            testName: testConf.title,
            date: new Date().toLocaleDateString('en-CA'),
            marksObtained: totalScore,
            totalMarks: maxScore,
            studentEmail: user.email.toLowerCase().trim(),
            
            // Subject Breakdown
            physicsScore: breakdowns.Physics.score,
            physicsMax: breakdowns.Physics.max,
            chemScore: breakdowns.Chemistry.score,
            chemMax: breakdowns.Chemistry.max,
            mathScore: breakdowns.Mathematics.score,
            mathMax: breakdowns.Mathematics.max,
            bioScore: breakdowns.Biology.score,
            bioMax: breakdowns.Biology.max
          };

          axios.post(
            `${API_BASE_URL}/api/students/email/${user.email.toLowerCase().trim()}/results`, 
            resultPayload,
            { 
              headers: { 
                'X-Tenant-ID': instituteId,
                'Authorization': `Bearer ${user.token}`
              } 
            }
          ).then(() => {
            toast.success("Test results synced successfully!");
          }).catch(e => {
            console.error("Result sync failed:", e);
            toast.error("Failed to sync results to server.");
          });
        }
      }
    } catch (e) { console.error(e); }
  }, [questions, answers, testConf, isSubmitted, instituteId]);

  // Window Leave detection
  useEffect(() => {
    if (isSubmitted || isInitializing) return;

    let outsideInterval;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        setWindowLeaveCount(prev => {
          const next = prev + 1;
          if (next > 3) {
            toast.error("Test auto-submitted: Exceeded maximum window leave limit (3 times).");
            handleSubmit();
          } else {
            toast.warning(`Warning: You left the test window. (${next}/3 times). Test will auto-submit on 4th attempt.`);
          }
          return next;
        });

        outsideInterval = setInterval(() => {
          setTotalOutsideTime(prev => {
            const next = prev + 1;
            if (next >= 30) {
              toast.error("Test auto-submitted: Spent more than 30 seconds outside the test window.");
              handleSubmit();
              clearInterval(outsideInterval);
            }
            return next;
          });
        }, 1000);
      } else {
        if (outsideInterval) clearInterval(outsideInterval);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (outsideInterval) clearInterval(outsideInterval);
    };
  }, [isSubmitted, isInitializing, handleSubmit]);

  useEffect(() => {
    if (timeLeft === null || isSubmitted) return;
    if (timeLeft <= 0) {
      handleSubmit();
      return;
    }
    const timerId = setInterval(() => {
      setTimeLeft(prev => prev - 1);
    }, 1000);
    return () => clearInterval(timerId);
  }, [timeLeft, isSubmitted, handleSubmit]);

  const handleNext = async () => {
    const activeQ = questions[currentIndex];
    const userAns = answers[activeQ.id];

    if (!userAns) {
      toast.info("Please select an option to move forward.");
      return;
    }

    // Is it time to end the test?
    const limit = testConf.questionLimit || 12;
    if (currentIndex + 1 >= limit) {
      handleSubmit();
      return;
    }

    // Adaptive Logic
    if (testConf.type === 'Adaptive') {
      setIsFetchingNext(true);
      
      const isCorrect = userAns === activeQ.correctOption;
      const newStageCorrect = isCorrect ? stageCorrectCount + 1 : stageCorrectCount;
      const stageLength = parseInt(adaptiveRules.stageLength) || 3;
      
      let nextDifficulty = currentDifficulty;

      // Evaluate stage completion
      if ((currentIndex + 1) % stageLength === 0) {
        const performance = (newStageCorrect / stageLength) * 100;
        const advanceThreshold = adaptiveRules.advanceThreshold || 70;
        const dropThreshold = adaptiveRules.dropThreshold || 40;

        let diffIdx = DIFFICULTY_LEVELS.indexOf(currentDifficulty);
        if (performance >= advanceThreshold && diffIdx < 2) {
          nextDifficulty = DIFFICULTY_LEVELS[diffIdx + 1];
        } else if (performance <= dropThreshold && diffIdx > 0) {
          nextDifficulty = DIFFICULTY_LEVELS[diffIdx - 1];
        }
        
        // Reset stage tracker
        setStageCorrectCount(0);
        setCurrentDifficulty(nextDifficulty);
      } else {
        setStageCorrectCount(newStageCorrect);
      }

      // Fetch next question
      const nextQ = await fetchQuestion(nextDifficulty, testConf.subjectFocus, testConf.targetClass, testConf.chapterFocus);
      if (nextQ) {
        setQuestions(prev => [...prev, nextQ]);
        seenIdsRef.current.push(nextQ.id);
        setCurrentIndex(prev => prev + 1);
      } else {
        handleSubmit(); // End test if no more questions
      }
      setIsFetchingNext(false);
    } else {
      // Normal test progression
      if (currentIndex + 1 < questions.length) {
        setCurrentIndex(prev => prev + 1);
      } else {
        handleSubmit();
      }
    }
  };

  const handleSelectOption = (qId, optText) => {
    if (isSubmitted || isFetchingNext) return;
    const newAnswers = { ...answers, [qId]: optText };
    setAnswers(newAnswers);
    answersRef.current = newAnswers;
  };

  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return h > 0 ? `${h}h ${m}m ${s}s` : `${m}m ${s}s`;
  };

  // Rendering Loader
  if (isInitializing || !testConf) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-8">
        <div className="w-16 h-16 border-4 border-t-indigo-500 border-slate-800 rounded-full animate-spin mb-4"></div>
        <div className="text-white animate-pulse text-xl font-medium">Securing Environment...</div>
      </div>
    );
  }

  // Rendering Scorecard
  if (isSubmitted && scoreData) {
    return (
      <div className="max-w-4xl mx-auto py-12 px-6 animate-in fade-in slide-in-from-bottom-4">
        <div className="bg-slate-900 border border-slate-700 p-8 rounded-3xl text-center shadow-2xl">
          <div className="w-20 h-20 bg-emerald-500/20 text-emerald-400 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">{testConf.title} - Session Summary</h1>
          <p className="text-slate-400 mb-8">Performance analysis across {questions.length} questions completed.</p>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8 text-left">
            <div className="bg-slate-800/50 p-4 rounded-2xl border border-slate-700">
              <div className="text-sm text-slate-400 mb-1">Final Score</div>
              <div className="text-3xl font-bold text-emerald-400">{scoreData.totalScore} <span className="text-lg text-slate-500">/ {scoreData.maxScore}</span></div>
            </div>
            <div className="bg-slate-800/50 p-4 rounded-2xl border border-slate-700">
              <div className="text-sm text-slate-400 mb-1">Accuracy</div>
              <div className="text-3xl font-bold text-sky-400">{scoreData.accuracy}%</div>
            </div>
            <div className="bg-slate-800/50 p-4 rounded-2xl border border-slate-700">
              <div className="text-sm text-slate-400 mb-1">Correct</div>
              <div className="text-3xl font-bold text-green-400">{scoreData.correct}</div>
            </div>
            <div className="bg-slate-800/50 p-4 rounded-2xl border border-slate-700">
              <div className="text-sm text-slate-400 mb-1">Incorrect</div>
              <div className="text-3xl font-bold text-rose-400">{scoreData.wrong}</div>
            </div>
            <div className="bg-slate-800/50 p-4 rounded-2xl border border-rose-500/20">
              <div className="text-sm text-rose-400 mb-1">Window Violations</div>
              <div className="text-3xl font-bold text-rose-400">{windowLeaveCount} <span className="text-lg text-slate-500">/ 3</span></div>
            </div>
            <div className="bg-slate-800/50 p-4 rounded-2xl border border-rose-500/20">
              <div className="text-sm text-rose-400 mb-1">Time Outside</div>
              <div className="text-3xl font-bold text-rose-400">{totalOutsideTime}s <span className="text-lg text-slate-500">/ 30s</span></div>
            </div>
          </div>
          
          <button onClick={() => navigate(`/${instituteId}/lms/tests`)} className="px-8 py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-bold transition-all shadow-lg shadow-indigo-500/20">
            Exit to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const currentQ = questions[currentIndex];
  if (!currentQ) return null;
  const currentOptions = [currentQ.optionA, currentQ.optionB, currentQ.optionC, currentQ.optionD].filter(Boolean);

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col pt-0 fixed inset-0 z-50">
      
      {/* Header */}
      <div className="bg-slate-900 border-b border-slate-800 px-8 py-4 flex items-center justify-between shrink-0 shadow-xl relative z-20">
        <div className="flex items-center gap-4">
          <div className="hidden md:block">
            <h1 className="text-xl font-bold text-white">{testConf.title}</h1>
            <p className="text-xs text-slate-500 uppercase tracking-widest font-bold">
              {testConf.type} Flow • Q{currentIndex + 1} of {testConf.questionLimit || questions.length}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-3 bg-slate-950/60 border border-slate-800 px-4 py-2 rounded-xl">
            <span className={`text-xl font-mono font-bold tracking-wider ${timeLeft < 300 ? 'text-rose-400' : 'text-amber-400'}`}>
              {formatTime(timeLeft)}
            </span>
          </div>
          
          <button 
            onClick={handleNext} 
            disabled={isFetchingNext}
            className="px-8 py-2.5 bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white rounded-xl font-bold transition-all shadow-lg shadow-sky-500/20 flex items-center gap-2"
          >
            {isFetchingNext ? 'Evaluating...' : (currentIndex + 1 === (testConf.questionLimit || 12) ? 'Submit Final' : 'Confirm & Next')}
            {!isFetchingNext && <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>}
          </button>
        </div>
      </div>

      {/* Main Container */}
      <div className="flex-1 flex flex-col md:flex-row overflow-hidden relative z-10">
        
        {/* Progress Sidebar (Desktop) */}
        <div className="hidden lg:block w-72 bg-slate-900 border-r border-slate-800 p-6 flex flex-col gap-6 overflow-y-auto">
          <div className="space-y-4">
            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest">Progress Tracker</h3>
            <div className="grid grid-cols-4 gap-2">
              {Array.from({ length: testConf.questionLimit || 12 }).map((_, i) => (
                <div 
                  key={i} 
                  className={`h-1.5 rounded-full transition-all ${
                    i < currentIndex ? 'bg-emerald-500' : i === currentIndex ? 'bg-indigo-500 animate-pulse' : 'bg-slate-800'
                  }`}
                ></div>
              ))}
            </div>
            <p className="text-[11px] text-slate-400">Total Progress: {Math.round(((currentIndex) / (testConf.questionLimit || 12)) * 100)}%</p>
          </div>

          <div className="p-4 rounded-2xl bg-indigo-500/5 border border-indigo-500/10 mt-auto">
             <h4 className="text-xs font-bold text-indigo-400 uppercase mb-2">Adaptive AI</h4>
             <p className="text-[10px] text-slate-400 leading-relaxed">System is analyzing accuracy and response speed to determine the optimal next challenge.</p>
             <div className="mt-3 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                <span className="text-[10px] text-emerald-500 font-bold uppercase">Logic Active</span>
             </div>
          </div>
        </div>

        {/* Question Area */}
        <div className="flex-1 overflow-y-auto bg-slate-950 p-6 md:p-12">
          <div className="max-w-4xl mx-auto animate-in fade-in slide-in-from-right-4 duration-500">
             <div className="mb-10 flex items-start justify-between gap-6">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-6">
                     <span className="px-3 py-1 bg-slate-800 text-slate-200 text-xs font-bold rounded-lg border border-slate-700">QUESTION {currentIndex + 1}</span>
                     <span className={`px-3 py-1 text-xs font-bold rounded-lg border uppercase ${
                        currentDifficulty === 'Hard' ? 'bg-rose-500/10 text-rose-500 border-rose-500/20' :
                        currentDifficulty === 'Medium' ? 'bg-amber-500/10 text-amber-500 border-amber-500/20' :
                        'bg-emerald-500/10 text-emerald-500 border-emerald-500/20'
                     }`}>{currentDifficulty}</span>
                  </div>
                  <div className="text-2xl text-slate-100 leading-relaxed font-medium">
                    <Latex>{currentQ.text || ''}</Latex>
                  </div>
                </div>
             </div>

             <div className="space-y-4">
               {currentOptions.map((opt, oIdx) => {
                 const isSelected = answers[currentQ.id] === opt;
                 const label = String.fromCharCode(65 + oIdx);
                 return (
                   <div 
                     key={oIdx}
                     onClick={() => handleSelectOption(currentQ.id, opt)}
                     className={`group flex items-center gap-5 p-5 rounded-2xl border transition-all cursor-pointer ${
                       isSelected 
                       ? 'bg-indigo-600/10 border-indigo-500 shadow-lg shadow-indigo-500/10' 
                       : 'bg-slate-900 border-slate-800 hover:border-slate-600 hover:bg-slate-800/50'
                     }`}
                   >
                     <div className={`w-10 h-10 rounded-xl border flex items-center justify-center font-bold text-lg shrink-0 transition-all ${
                       isSelected ? 'bg-indigo-500 border-indigo-500 text-white' : 'bg-slate-800 border-slate-700 text-slate-400 group-hover:border-slate-500'
                     }`}>
                       {label}
                     </div>
                     <div className={`flex-1 text-lg transition-colors ${isSelected ? 'text-white' : 'text-slate-300'}`}>
                       <Latex>{opt || ''}</Latex>
                     </div>
                   </div>
                 )
               })}
             </div>
          </div>
        </div>

      </div>

      {/* Footer Mobile Info */}
      <div className="block lg:hidden bg-slate-900 border-t border-slate-800 p-4 shrink-0 text-center">
         <p className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">Progress: {currentIndex + 1} / {testConf.questionLimit || 12}</p>
      </div>
    </div>
  );
}
